package com.tovtalk.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.tovtalk.ui.theme.TovTalkTheme

private val storyRingColors = listOf(
    Color(0xFF34C759),
    Color(0xFF30D158),
)

/**
 * Avatar component - displays image or placeholder.
 * Image loading uses platform-specific implementation (Kamel/Coil).
 */
@Composable
fun TovTalkAvatar(
    imageUrl: String?,
    modifier: Modifier = Modifier,
    size: Dp = TovTalkTheme.sizes.avatarMd,
    placeholder: ImageVector? = null,
    contentDescription: String? = null,
    name: String? = null,
    onClick: (() -> Unit)? = null,
    hasStory: Boolean = false,
    onStoryClick: (() -> Unit)? = null,
) {
    val tap = if (hasStory) (onStoryClick ?: onClick) else onClick
    val ringWidth = 2.dp
    val ringGap = 2.dp
    val outer = if (hasStory) size + (ringWidth + ringGap) * 2 else size

    Box(
        modifier = modifier
            .size(outer)
            // Story ring border is outermost (before clip) so it is never clipped away.
            .then(
                if (hasStory) Modifier.border(
                    width = ringWidth,
                    brush = Brush.linearGradient(storyRingColors),
                    shape = CircleShape
                ) else Modifier
            )
            // Clip to circle BEFORE clickable so the ripple indicator is bounded to the
            // avatar circle in both hasStory paths. The border above is unaffected.
            .clip(CircleShape)
            .then(if (tap != null) Modifier.clickable(onClick = tap) else Modifier),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .size(size)
                .clip(TovTalkTheme.shapes.avatar)
                .background(TovTalkTheme.colors.surfaceElevated),
            contentAlignment = Alignment.Center
        ) {
            when {
                !imageUrl.isNullOrBlank() -> TovTalkAsyncImage(
                    url = imageUrl,
                    modifier = Modifier.size(size)
                )
                placeholder != null -> Icon(
                    imageVector = placeholder,
                    contentDescription = contentDescription,
                    modifier = Modifier.size(size * 0.5f),
                    tint = TovTalkTheme.colors.textTertiary
                )
                !name.isNullOrBlank() -> {
                    val initials = name.take(2).uppercase()
                    Text(
                        text = initials,
                        style = TovTalkTheme.typography.titleMedium,
                        color = TovTalkTheme.colors.textSecondary,
                        textAlign = TextAlign.Center
                    )
                }
                else -> Icon(
                    imageVector = Icons.Default.Person,
                    contentDescription = contentDescription,
                    modifier = Modifier.size(size * 0.5f),
                    tint = TovTalkTheme.colors.textTertiary
                )
            }
        }
    }
}

@Composable
expect fun TovTalkAsyncImage(
    url: String,
    modifier: Modifier,
    clipToCircle: Boolean = true,
    contentScale: androidx.compose.ui.layout.ContentScale = androidx.compose.ui.layout.ContentScale.Fit
)
